import React, { Component, PropsWithChildren } from 'react';
import styled from 'styled-components';

interface Props {
  hideError?: boolean;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

const DevErrorInfo = styled.pre`
  color: red;
`;

const ProdErrorInfo = styled.div`
  color: var(--way-palette-black-70);
`;

const EmptyErrorContainer = styled.div`
  height: 50px;
  width: 100%;
  background-color: var(--way-palette-black-70);
`;

export class ErrorBoundary extends Component<PropsWithChildren<Props>, State> {
  constructor(props: PropsWithChildren<Props>) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log the error to console
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    this.props.onError?.(error, errorInfo);

    this.setState({
      error,
    });
  }

  render() {
    const { hasError, error } = this.state;
    const { children, hideError } = this.props;

    if (hasError) {
      if (hideError) {
        return (
          <>
            <EmptyErrorContainer />
            {children}
          </>
        );
      }

      return (
        <>
          <ProdErrorInfo>Something went wrong.</ProdErrorInfo>
          {process.env.NODE_ENV === 'development' && error && (
            <DevErrorInfo>
              {error.toString()}
              {error.stack}
            </DevErrorInfo>
          )}
          {children}
        </>
      );
    }
    return children;
  }
}
